import { useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { MapPin, SlidersHorizontal, Sparkles } from 'lucide-react';
import { EventCard } from '../components/Events/EventCard';
import { AddressSearch } from '../components/Filters/AddressSearch';
import { HorizontalFilterBar } from '../components/Listings/Filters/HorizontalFilterBar';
import { SkeletonGrid } from '../components/ui/SkeletonCard';
import { EventForm } from '../components/Listings/Forms/EventForm';
import { Hero } from '../components/Hero';
import { Meta } from '../components/Meta';
import { supabase } from '../lib/supabase';
import { europeanCountries } from '../lib/constants/countries';
import { getCurrentLocation, filterByDistance, calculateDistance, type LocationCoords } from '../lib/utils/locationFilter';
import type { Event } from '../types/events';
import type { Vibe, SubCategory } from '../types/vibes';
import { DEFAULT_VIBES } from '../types/vibes';

interface Filters {
  subCategories: string[];
  countries: string[];
  nearMe: boolean;
  suitableForKids: boolean;
}

type TimeFilterOption =
  | 'any'
  | 'starts_soon'
  | 'today'
  | 'tomorrow'
  | 'this_week'
  | 'this_weekend'
  | 'next_week'
  | 'custom';

const startOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const addDays = (date: Date, days: number) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const startOfWeekMonday = (date: Date) => {
  const d = startOfDay(date);
  const day = d.getDay(); // 0=Sun, 1=Mon, ...
  const diffToMonday = (day + 6) % 7;
  return addDays(d, -diffToMonday);
};

const formatDateYmdLocal = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const matchesTimeFilter = (eventStartDate: string, timeFilter: TimeFilterOption, customDate: string) => {
  if (timeFilter === 'any') return true;

  const eventDate = new Date(eventStartDate);
  if (Number.isNaN(eventDate.getTime())) return false;

  const now = new Date();
  const todayStart = startOfDay(now);
  const todayEnd = endOfDay(now);
  const tomorrowStart = startOfDay(addDays(now, 1));
  const tomorrowEnd = endOfDay(addDays(now, 1));
  const thisWeekStart = startOfWeekMonday(now);
  const thisWeekEnd = endOfDay(addDays(thisWeekStart, 6));
  const weekendStart = startOfDay(addDays(thisWeekStart, 5)); // Saturday
  const weekendEnd = endOfDay(addDays(thisWeekStart, 6)); // Sunday
  const nextWeekStart = startOfDay(addDays(thisWeekStart, 7));
  const nextWeekEnd = endOfDay(addDays(thisWeekStart, 13));

  switch (timeFilter) {
    case 'starts_soon': {
      return eventDate >= todayStart && eventDate <= todayEnd;
    }
    case 'today':
      return eventDate >= todayStart && eventDate <= todayEnd;
    case 'tomorrow':
      return eventDate >= tomorrowStart && eventDate <= tomorrowEnd;
    case 'this_week': {
      const windowStart = now > thisWeekStart ? now : thisWeekStart;
      return eventDate >= windowStart && eventDate <= thisWeekEnd;
    }
    case 'this_weekend': {
      const windowStart = now > weekendStart ? now : weekendStart;
      return eventDate >= windowStart && eventDate <= weekendEnd;
    }
    case 'next_week':
      return eventDate >= nextWeekStart && eventDate <= nextWeekEnd;
    case 'custom':
      return customDate ? formatDateYmdLocal(eventDate) === customDate : true;
    default:
      return true;
  }
};

const sortByStatusAndProximity = <T extends Event & { distance?: number }>(items: T[]) => {
  const now = Date.now();
  return [...items].sort((a, b) => {
    const endA = new Date(a.end_date || a.start_date).getTime();
    const endB = new Date(b.end_date || b.start_date).getTime();
    const isOverA = endA < now;
    const isOverB = endB < now;

    // Keep ended events after non-ended events.
    if (isOverA !== isOverB) {
      return isOverA ? 1 : -1;
    }

    const hasDistanceA = typeof a.distance === 'number';
    const hasDistanceB = typeof b.distance === 'number';

    // Events with computed distance should be prioritized.
    if (hasDistanceA !== hasDistanceB) {
      return hasDistanceA ? -1 : 1;
    }

    if (hasDistanceA && hasDistanceB) {
      const distanceDiff = (a.distance as number) - (b.distance as number);
      if (distanceDiff !== 0) return distanceDiff;
    }

    // Fallback to closest date to now.
    const startA = new Date(a.start_date).getTime();
    const startB = new Date(b.start_date).getTime();
    return Math.abs(startA - now) - Math.abs(startB - now);
  });
};

const attachDistance = <T extends Event>(items: T[], location: LocationCoords): (T & { distance?: number })[] => {
  return items.map((item) => {
    if (item.latitude == null || item.longitude == null) {
      return { ...item };
    }
    return {
      ...item,
      distance: calculateDistance(location.latitude, location.longitude, item.latitude, item.longitude)
    };
  });
};

export function Events() {
  const EVENTS_PER_PAGE = 24;
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<(Event & { distance?: number; vibe?: Vibe; subCategory?: SubCategory })[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMoreServerResults, setHasMoreServerResults] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [userLocation, setUserLocation] = useState<LocationCoords | null>(null);
  const [ipLocation, setIpLocation] = useState<LocationCoords | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    subCategories: [],
    countries: [],
    nearMe: false,
    suitableForKids: false
  });

  // Vibe filter state
  const [vibes, setVibes] = useState<Vibe[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [selectedVibes, setSelectedVibes] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [timeFilter, setTimeFilter] = useState<TimeFilterOption>('any');
  const [customDate, setCustomDate] = useState('');
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [locationInput, setLocationInput] = useState('');
  const searchQueryParam = searchParams.get('q')?.trim() || '';
  const locationParamRaw = searchParams.get('location')?.trim() || '';
  const locationKeyword = locationParamRaw.split(',')[0]?.trim() || locationParamRaw;

  // Fetch vibes and sub-categories
  useEffect(() => {
    async function fetchVibes() {
      const { data: vibesData } = await supabase
        .from('vibes')
        .select('*')
        .order('display_order');

      const { data: subCatsData } = await supabase
        .from('sub_categories')
        .select('*')
        .order('display_order');

      if (vibesData) setVibes(vibesData);
      if (subCatsData) setSubCategories(subCatsData);
    }
    fetchVibes();
  }, []);

  // Get approximate user location from IP (no browser permission prompt)
  useEffect(() => {
    let isCancelled = false;

    async function fetchIpLocation() {
      try {
        const response = await fetch('https://ipapi.co/json/');
        if (!response.ok) return;
        const data: { latitude?: number; longitude?: number } = await response.json();
        if (!isCancelled && typeof data.latitude === 'number' && typeof data.longitude === 'number') {
          setIpLocation({ latitude: data.latitude, longitude: data.longitude });
        }
      } catch {
        // Silent fallback: keep existing date-based sorting when IP lookup fails.
      }
    }

    fetchIpLocation();

    return () => {
      isCancelled = true;
    };
  }, []);

  const userHasInteracted = useRef(false);

  // Initialize filters from URL parameters
  useEffect(() => {
    const countryParam = searchParams.get('country');

    if (countryParam) {
      setFilters(prev => ({
        ...prev,
        countries: [countryParam]
      }));
    }
    setSearchQuery(searchQueryParam);

    const keyword = locationKeyword;
    if (keyword) {
      setLocationInput(keyword);
    }
  }, [searchParams]);

  useEffect(() => {
    setPage(0);
    setEvents([]);
  }, [filters, searchParams, refreshKey]);

  useEffect(() => {
    async function fetchEvents() {
      try {
        if (page === 0) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }

        // Only use URL parameter if user hasn't interacted with filters yet
        const countryParam = searchParams.get('country');
        const effectiveFilters = (countryParam && !userHasInteracted.current)
          ? { ...filters, countries: [countryParam] }
          : filters;

        let query = supabase
          .from('events')
          .select(`
            *,
            user:users (
              id,
              username,
              full_name,
              avatar_url,
              user_type,
              subscription_status,
              manual_professional
            ),
            community:communities (
              id,
              name,
              avatar_url
            )
          `)
          .eq('approval_status', 'approved')
          .or('visibility.eq.public,visibility.is.null')
          .eq('user.user_type', 'professional');

        if (effectiveFilters.subCategories.length > 0) {
          query = query.in('sub_category_id', effectiveFilters.subCategories);
        }

        if (effectiveFilters.countries.length > 0) {
          const expandedCountries = Array.from(new Set(
            effectiveFilters.countries.flatMap((c) => {
              const match = europeanCountries.find(ec => ec.value === c || ec.label === c);
              return match ? [match.value, match.label] : [c];
            })
          ));
          query = query.in('country', expandedCountries);
        }
        const effectiveLocationKeyword = (locationInput || locationKeyword).trim();
        if (effectiveLocationKeyword) {
          query = query.or(`location.ilike.%${effectiveLocationKeyword}%,country.ilike.%${effectiveLocationKeyword}%`);
        }

        query = query
          .order('start_date', { ascending: true })
          .range(page * EVENTS_PER_PAGE, page * EVENTS_PER_PAGE + EVENTS_PER_PAGE - 1);

        const { data, error } = await query;
        if (error) throw error;
        const rows = data || [];
        setHasMoreServerResults(rows.length === EVENTS_PER_PAGE);
        setEvents(prev => (page === 0 ? rows : [...prev, ...rows]));
      } catch (err: any) {
        setError(err.message);
      } finally {
        if (page === 0) {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    }

    fetchEvents();
  }, [filters, searchParams, refreshKey, page, locationInput, locationKeyword]);

  // Apply filters client-side (vibes, search, near me)
  useEffect(() => {
    let filtered = [...events];

    // Apply vibe filters
    if (selectedVibes.length > 0) {
      filtered = filtered.filter(event =>
        selectedVibes.includes(event.primary_vibe || '') ||
        (event.vibe_tags && event.vibe_tags.some((tag: string) => selectedVibes.includes(tag)))
      );
    }

    // Apply category filters (includes primary sub_category AND extra tags)
    if (filters.subCategories.length > 0) {
      filtered = filtered.filter(event => {
        const hasPrimarySubCategory = event.sub_category_id && filters.subCategories.includes(event.sub_category_id);
        const hasTagInExtras = event.vibe_tags && event.vibe_tags.some((tag: string) => filters.subCategories.includes(tag));
        return hasPrimarySubCategory || hasTagInExtras;
      });
    }

    // Apply search filter
    const effectiveSearchQuery = (searchQuery || searchQueryParam).trim();
    if (effectiveSearchQuery) {
      const query = effectiveSearchQuery.toLowerCase();
      filtered = filtered.filter(event =>
        event.title?.toLowerCase().includes(query) ||
        event.description?.toLowerCase().includes(query) ||
        event.location?.toLowerCase().includes(query) ||
        event.organiser_business_name?.toLowerCase().includes(query)
      );
    }

    const effectiveLocationKeyword = (locationInput || locationKeyword).trim();
    if (effectiveLocationKeyword) {
      const keyword = effectiveLocationKeyword.toLowerCase();
      filtered = filtered.filter(event =>
        event.location?.toLowerCase().includes(keyword) ||
        event.country?.toLowerCase().includes(keyword)
      );
    }

    // Apply near me filter
    if (filters.nearMe && userLocation) {
      filtered = filterByDistance(filtered, userLocation, 100);
    }

    // Apply family-friendly filter
    if (filters.suitableForKids) {
      filtered = filtered.filter(event => event.suitable_for_kids === true);
    }

    // Apply time filter
    filtered = filtered.filter(event => matchesTimeFilter(event.start_date, timeFilter, customDate));

    // Attach vibe info to events for display
    filtered = filtered.map(event => {
      const vibe = vibes.find(v => v.id === event.primary_vibe);
      const subCategory = subCategories.find(sc => sc.id === event.sub_category_id);
      return { ...event, vibe, subCategory };
    });

    // Default behavior: prioritize nearby events from IP location.
    // If user has enabled "Near Me", use their explicit geolocation instead.
    const referenceLocation = userLocation || ipLocation;
    let sortedForDisplay = filtered;

    if (referenceLocation) {
      sortedForDisplay = attachDistance(sortedForDisplay, referenceLocation);
    }

    sortedForDisplay = sortByStatusAndProximity(sortedForDisplay);

    setFilteredEvents(sortedForDisplay);
  }, [events, filters.nearMe, filters.suitableForKids, filters.subCategories, userLocation, ipLocation, selectedVibes, searchQuery, searchQueryParam, locationKeyword, locationInput, timeFilter, customDate, vibes, subCategories]);

  const handleFilterChange = async (filterType: keyof Filters, values: string[] | boolean) => {
    // Mark that user has interacted with filters
    userHasInteracted.current = true;

    // Handle Near Me filter specially
    if (filterType === 'nearMe' && values === true && !userLocation) {
      setLocationLoading(true);
      try {
        const location = await getCurrentLocation();
        setUserLocation(location);
        setFilters(prev => ({ ...prev, nearMe: true }));
      } catch (err: any) {
        setError(err.message);
        setFilters(prev => ({ ...prev, nearMe: false }));
      } finally {
        setLocationLoading(false);
      }
      return;
    }

    setFilters(prev => ({
      ...prev,
      [filterType]: values
    }));
  };

  const handleAddressSelect = (latitude: number, longitude: number, address: string) => {
    setUserLocation({ latitude, longitude });
    setFilters(prev => ({ ...prev, nearMe: true }));
  };

  const handleCreateListing = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      window.dispatchEvent(new CustomEvent('show-auth'));
    } else {
      setShowCreateModal(true);
    }
  };

  const displayEvents = filteredEvents;
  const hasMoreEvents = hasMoreServerResults;
  const activeFilterCount = useMemo(() => {
    let count = 0;
    count += selectedVibes.length;
    count += filters.subCategories.length;
    count += filters.countries.length;
    if (filters.nearMe) count += 1;
    if (filters.suitableForKids) count += 1;
    if (timeFilter !== 'any') count += 1;
    if (searchQuery.trim()) count += 1;
    if (locationInput.trim()) count += 1;
    return count;
  }, [
    selectedVibes.length,
    filters.subCategories.length,
    filters.countries.length,
    filters.nearMe,
    filters.suitableForKids,
    timeFilter,
    searchQuery,
    locationInput
  ]);

  const clearAllDiscoveryFilters = () => {
    setSelectedVibes([]);
    setSearchQuery('');
    setLocationInput('');
    setShowMoreFilters(false);
    setFilters(prev => ({
      ...prev,
      subCategories: [],
      countries: [],
      nearMe: false,
      suitableForKids: false
    }));
    setTimeFilter('any');
    setCustomDate('');
  };

  return (
    <div>
      <Meta
        title={`${t('events.title')} | Mindful Family`}
        description={t('events.subtitle')}
      />

      {/* Mobile Full-Width Header */}
      <div className="lg:hidden relative h-64 overflow-hidden">
        <img
          src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/listing-images/123c446f-e80c-409d-a3d3-e6fdc14949d4/wellness-event.png"
          alt="Discover Wellness Events"
          className="w-full h-full object-cover object-top shadow-none"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-50"></div>
      </div>

      {/* Mobile Content Below Image */}
      <div className="lg:hidden px-4 py-8 text-center bg-gray-50">
        <h1 className="text-3xl font-gelica font-bold text-content mb-4">
          {t('events.title')}
        </h1>
        <p className="text-lg text-content/70 mb-6 max-w-md mx-auto">
          {t('events.subtitle')}
        </p>
        <button
          onClick={() => {
            const eventsSection = document.querySelector('[data-events-section]');
            if (eventsSection) {
              eventsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }}
          className="inline-flex items-center px-6 py-3 bg-accent-text text-white rounded-full font-medium hover:bg-accent-text/90 transition-colors shadow-sm mb-3 whitespace-nowrap"
        >
          {t('events.browseEvents')}
        </button>
        <div>
          <button
            onClick={handleCreateListing}
            className="text-sm text-accent-text hover:text-accent-text/80 underline"
          >
            {t('events.areYouOrganizer')}
          </button>
        </div>
      </div>

      {/* Desktop Hero */}
      <div className="hidden lg:block relative mb-12 bg-gradient-to-b from-[#F3F7EE] via-[#F8FAF5] to-white overflow-hidden">
        <div className="py-8 lg:py-16 relative z-10">
          <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="text-center lg:text-left">
                <h1 className="text-4xl lg:text-5xl font-gelica leading-tight mb-4 lg:mb-6">{t('events.title')}</h1>
                <p className="text-lg lg:text-xl text-gray-600 mb-6 lg:mb-8">{t('events.subtitle')}</p>
                <div className="mb-3">
                  <button
                    onClick={() => {
                      const eventsSection = document.querySelector('[data-events-section]');
                      if (eventsSection) {
                        eventsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    className="inline-flex items-center px-6 lg:px-10 py-3 bg-[#8DA847] text-white rounded-full font-medium hover:bg-[#7A9339] transition-colors whitespace-nowrap"
                  >
                    {t('events.browseEvents')}
                  </button>
                </div>
                <div>
                  <button
                    onClick={handleCreateListing}
                    className="text-sm text-accent-text hover:text-accent-text/80 underline"
                  >
                    {t('events.areYouOrganizer')}
                  </button>
                </div>
              </div>
      <div className="hidden lg:block">
                <img
                  src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/listing-images/123c446f-e80c-409d-a3d3-e6fdc14949d4/wellness-event.png"
                  alt="Discover Transformative Wellness Events"
                  className="w-full h-[450px] object-cover blob-shape"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-0 sm:px-6 lg:px-10 mt-16 lg:mt-8" data-events-section>
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-8">
          <div className="bg-white rounded-2xl border border-accent-text/10 shadow-sm p-4 lg:p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="h-4 w-4 text-accent-text" />
                <p className="text-sm font-semibold text-[#2F4F3E]">Find your best-matching event</p>
                {activeFilterCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-accent-text text-white">
                    {activeFilterCount} active
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowMoreFilters(prev => !prev)}
                  className={`inline-flex items-center gap-2 h-9 px-3 rounded-xl text-sm font-medium transition-colors ${
                    showMoreFilters
                      ? 'bg-accent-text text-white'
                      : 'bg-accent-text/5 text-[#2F4F3E] border border-accent-text/15 hover:bg-accent-text/10'
                  }`}
                >
                  <Sparkles className="h-4 w-4" />
                  {showMoreFilters ? 'Hide more filters' : 'More filters'}
                </button>
                {activeFilterCount > 0 && (
                  <button
                    onClick={clearAllDiscoveryFilters}
                    className="h-9 px-3 rounded-xl text-sm font-medium text-accent-text border border-accent-text/20 hover:bg-accent-text/5 transition-colors"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-accent-text/10 bg-[#F8FAF5] p-3 lg:p-4">
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-2 lg:gap-3 text-sm text-[#2F4F3E]">
                  <span className="font-semibold whitespace-nowrap">I am looking for</span>
                  <select
                    value={filters.subCategories[0] || ''}
                    onChange={(e) => {
                      const next = e.target.value;
                      handleFilterChange('subCategories', next ? [next] : []);
                    }}
                    className="h-10 px-3 rounded-xl border border-accent-text/15 bg-white text-[#2F4F3E] focus:outline-none focus:ring-2 focus:ring-accent-text/20 min-w-[220px]"
                  >
                    <option value="">{t('filters.all', 'All')}</option>
                    {subCategories
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((sc) => (
                        <option key={sc.id} value={sc.id}>
                          {sc.name}
                        </option>
                      ))}
                  </select>

                  <span className="font-semibold whitespace-nowrap">in</span>

                  <div className="relative w-full sm:w-72">
                    <input
                      type="text"
                      value={locationInput}
                      onChange={(e) => {
                        const next = e.target.value;
                        setLocationInput(next);
                        if (filters.nearMe && next.trim()) {
                          handleFilterChange('nearMe', false);
                        }
                      }}
                      placeholder={filters.nearMe ? t('filters.nearMe') : t('filters.location', 'Location')}
                      className="h-10 w-full pr-11 px-4 rounded-xl border border-accent-text/15 bg-white text-[#2F4F3E] placeholder:text-[#5A6B5A]/60 focus:outline-none focus:ring-2 focus:ring-accent-text/20"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setLocationInput('');
                        handleFilterChange('nearMe', true);
                      }}
                      disabled={locationLoading}
                      className={`absolute right-1.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-lg inline-flex items-center justify-center transition-colors ${
                        filters.nearMe
                          ? 'bg-accent-text text-white'
                          : 'bg-accent-text/5 text-[#2F4F3E] hover:bg-accent-text/10'
                      } ${locationLoading ? 'opacity-50 cursor-wait' : ''}`}
                      aria-label={t('filters.nearMe')}
                    >
                      <MapPin className="h-4 w-4" />
                    </button>
                  </div>

                  <span className="font-semibold whitespace-nowrap">at</span>

                  <select
                    value={timeFilter}
                    onChange={(e) => {
                      const next = e.target.value as TimeFilterOption;
                      setTimeFilter(next);
                      if (next !== 'custom') setCustomDate('');
                    }}
                    className="h-10 px-3 rounded-xl border border-accent-text/15 bg-white text-[#2F4F3E] focus:outline-none focus:ring-2 focus:ring-accent-text/20"
                  >
                    <option value="any">{t('events.timeFilters.anyDay')}</option>
                    <option value="starts_soon">{t('events.timeFilters.startsSoon')}</option>
                    <option value="today">{t('events.timeFilters.today')}</option>
                    <option value="tomorrow">{t('events.timeFilters.tomorrow')}</option>
                    <option value="this_week">{t('events.timeFilters.thisWeek')}</option>
                    <option value="this_weekend">{t('events.timeFilters.thisWeekend')}</option>
                    <option value="next_week">{t('events.timeFilters.nextWeek')}</option>
                    <option value="custom">{t('events.timeFilters.custom')}</option>
                  </select>
                  {timeFilter === 'custom' && (
                    <input
                      type="date"
                      value={customDate}
                      onChange={(e) => setCustomDate(e.target.value)}
                      className="h-10 px-3 rounded-xl border border-accent-text/15 bg-white text-[#2F4F3E] focus:outline-none focus:ring-2 focus:ring-accent-text/20"
                    />
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedVibes([])}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 active:scale-[0.97] ${
                      selectedVibes.length === 0
                        ? 'bg-accent-text text-white shadow-sm'
                        : 'bg-white text-[#2F4F3E] hover:bg-accent-text/5 border border-accent-text/10'
                    }`}
                  >
                    {t('filters.any', 'Any mood')}
                  </button>
                  {(vibes.length > 0 ? vibes : DEFAULT_VIBES).map((v) => {
                    const isSelected = selectedVibes.includes(v.id);
                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => {
                          setSelectedVibes((prev) => (prev.includes(v.id) ? prev.filter((id) => id !== v.id) : [...prev, v.id]));
                        }}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 active:scale-[0.97] ${
                          isSelected
                            ? 'bg-accent-text text-white shadow-sm'
                            : 'bg-white text-[#2F4F3E] hover:bg-accent-text/5 border border-accent-text/10'
                        }`}
                      >
                        {v.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {showMoreFilters && (
              <div className="mt-4 pt-4 border-t border-accent-text/10 space-y-3">
                <div className="max-w-md">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search…"
                    className="h-10 w-full px-4 rounded-xl border border-accent-text/15 bg-white text-[#2F4F3E] placeholder:text-[#5A6B5A]/60 focus:outline-none focus:ring-2 focus:ring-accent-text/20"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleFilterChange('suitableForKids', !filters.suitableForKids)}
                    className={`h-10 px-4 rounded-xl inline-flex items-center gap-2 text-sm font-medium transition-colors ${
                      filters.suitableForKids
                        ? 'bg-[#C4714A] text-white'
                        : 'bg-accent-text/5 text-[#2F4F3E] border border-accent-text/15 hover:bg-accent-text/10'
                    }`}
                  >
                    <span>Kids 5+</span>
                  </button>
                </div>

                <div className="max-w-md">
                  <AddressSearch onAddressSelect={handleAddressSelect} />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
          <div className="mt-3 mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-[#5A6B5A]">
              {loading ? 'Loading events...' : `${filteredEvents.length} events found`}
            </p>
            {ipLocation && !filters.nearMe && (
              <span className="text-xs text-[#5A6B5A] bg-accent-text/5 border border-accent-text/10 rounded-full px-3 py-1">
                Ordered by nearby first
              </span>
            )}
          </div>
          <div className="mt-2">
          {error && (
            <div className="text-red-600 mb-4">{error}</div>
          )}

          {loading ? (
            <div className="px-4 sm:px-0">
              <SkeletonGrid count={6} />
            </div>
          ) : displayEvents.length === 0 ? (
            <div className="bg-[#F8FAF5] rounded-2xl p-12 text-center border border-accent-text/10">
              <p className="text-[#2F4F3E] font-medium">
                {filters.nearMe ? t('events.noEventsNearby') : t('events.noEventsFilter')}
              </p>
              <p className="text-[#5A6B5A] text-sm mt-2">Try adjusting your filters to discover more</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-4 sm:px-0">
                {displayEvents.map((event) => (
                  <EventCard key={event.id} event={event} distance={(event as any).distance} />
                ))}
              </div>
              {hasMoreEvents && (
                <div className="flex justify-center mt-10">
                  <button
                    onClick={() => setPage(prev => prev + 1)}
                    disabled={loadingMore}
                    className="px-8 py-3 bg-accent-text text-white rounded-xl font-medium hover:bg-accent-text/90 transition-all duration-200 active:scale-[0.98] text-sm disabled:opacity-60"
                  >
                    {loadingMore ? 'Loading...' : 'Show more'}
                  </button>
                </div>
              )}
            </>
          )}
          </div>
        </div>
      </div>

      {showCreateModal && (
        <EventForm
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            setRefreshKey(k => k + 1);
          }}
        />
      )}
    </div>
  );
}
